/**
 * Hook to read+drive Sam's conversation state for a given activity.
 * Owns the conversation as displayed (Redux), the optimistic add on send,
 * and the UI knobs the screen needs (sendText, askHint, askCourseReminder).
 * The conversation Sam reads is the one the server keeps: only the new
 * message is sent.
 *
 * The server is authoritative on cap/degraded — this hook only mirrors
 * what comes back, plus the daily cap kept for the day across activities.
 *
 * Déplacé de `student/sam-sessions/hooks/` (mêmes envois, mêmes mesures), avec un geste de plus,
 * sans effet sur le legacy qui ne le lit pas : un message de l'élève resté sans réponse parce que
 * Sam est en panne (le fil passe en dégradé) est désigné (`failedMessageId`), et `retryMessage` le
 * renvoie — il quitte le fil, l'envoi le repose. Un plafond ou un refus du serveur (429, message
 * trop long) n'est pas une panne : Sam y répond déjà, rien ne se renvoie.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"

import { SAM_EMPTY_CONVERSATION, SAM_TRIGGERS, samDayKey } from "../../../data/constants/samConstants"
import { apiRequest } from "../../../data/redux/actions/api"
import { dropSamMessage, resetSamConversation } from "../../../data/redux/actions/data"
import { samService } from "../../../data/services/samService"
import { requestOptionsFor } from "../../shared/apiCall"

/** Un message du fil de Sam, tel que le réducteur `sam` le garde. */
export type SamMessage = {
    id: string
    role: "student" | "sam"
    content?: string
    mood?: string
    origin?: string
    kind?: string
    imageUri?: string
    createdAt?: string
}

export type SamConversationState = {
    messages: SamMessage[]
    isThinking: boolean
    conversationUuid: string | null
    capReached: boolean
    degraded: boolean
    lastMood?: string
}

type SamSendFailure = {
    status?: number
    error?: string
    errors?: unknown
    data?: { cap_reached?: boolean } | null
}

type SamRoot = {
    data?: {
        sam?: {
            conversationByActivityId?: Record<string, SamConversationState>
            dailyCapReachedOn?: string | null
        }
    }
}

type SendParams = { message: string; origin?: string; trigger?: string }

/**
 * Une panne de Sam, au sens du réducteur : ce qui n'est ni un plafond (422 `cap_reached`) ni un refus
 * dit en mots (429, 422 avec des erreurs de validation). C'est le seul échec qui laisse le message de
 * l'élève sans réponse, donc le seul qui se renvoie.
 */
export function isSamOutage(error: SamSendFailure | null | undefined): boolean {
    if (error == null) return false
    if (error.error === "cap_reached" || error.data?.cap_reached) return false
    if (error.status === 429) return false
    if (error.status === 422 && error.errors) return false
    return true
}

export function useSamConversation(activityId: number | null, options: { offlineDialog?: boolean } = {}) {
    const offlineDialog = options.offlineDialog ?? true
    const dispatch = useDispatch()
    const store = useStore<SamRoot>()

    const conversation = useSelector(
        (state: SamRoot) =>
            (activityId != null ? state.data?.sam?.conversationByActivityId?.[activityId] : undefined) ||
            (SAM_EMPTY_CONVERSATION as SamConversationState)
    )
    const dailyCapReached = useSelector((state: SamRoot) => state.data?.sam?.dailyCapReachedOn === samDayKey())
    const capReached = conversation.capReached || dailyCapReached

    // Lu au moment de l'envoi plutôt que capté par les callbacks : sans ça,
    // chaque message recréait toutes les actions de la feuille.
    const conversationRef = useRef(conversation)
    useEffect(() => {
        conversationRef.current = conversation
    }, [conversation])

    // Le message resté sans réponse, et ce qu'il faut pour le renvoyer à l'identique.
    const [failed, setFailed] = useState<{ id: string; params: SendParams } | null>(null)

    const dispatchSend = useCallback(
        ({ message, origin = "text", trigger = SAM_TRIGGERS.freeText }: SendParams) => {
            if (!activityId || !message?.trim?.()) return
            amplitude.track("sam.message.sent", {
                activity_id: activityId,
                trigger,
                origin,
                history_length: conversationRef.current.messages.length,
            })
            setFailed(null)
            const params = { message, origin, trigger }
            dispatch(
                apiRequest(
                    samService.sendSamMessage,
                    { activity_id: activityId },
                    {
                        message: message.trim(),
                        message_origin: origin,
                        trigger,
                        conversation_uuid: conversationRef.current.conversationUuid,
                    },
                    {},
                    null,
                    null,
                    {
                        ...requestOptionsFor(offlineDialog),
                        onResult: (result: { ok: boolean; error?: SamSendFailure }) => {
                            if (result.ok || !isSamOutage(result.error)) return
                            // Le réducteur a posé le message de l'élève à la demande, puis passé le fil en
                            // dégradé : c'est le dernier du fil. Hors ligne, la demande n'est pas partie et
                            // rien n'a été posé : il n'y a pas de message à désigner.
                            const messages =
                                store.getState().data?.sam?.conversationByActivityId?.[activityId]?.messages ?? []
                            const last = messages[messages.length - 1]
                            if (last?.role === "student" && last.content === message.trim()) {
                                setFailed({ id: last.id, params })
                            }
                        },
                    }
                ) as never
            )
        },
        [activityId, dispatch, offlineDialog, store]
    )

    // Surface server-driven flag transitions (cap reached / degraded mode)
    // as Amplitude events. We only fire on the rising edge so reopening the
    // sheet doesn't refire — the ref persists for the hook's lifetime.
    const lastCapRef = useRef(false)
    const lastDegradedRef = useRef(false)
    useEffect(() => {
        if (capReached && !lastCapRef.current) {
            amplitude.track("sam.cap_reached", {
                activity_id: activityId,
                messages_count: conversation.messages.length,
                scope: dailyCapReached ? "daily" : "activity",
            })
        }
        lastCapRef.current = !!capReached
    }, [capReached, dailyCapReached, conversation.messages.length, activityId])
    useEffect(() => {
        if (conversation.degraded && !lastDegradedRef.current) {
            amplitude.track("sam.degraded", {
                activity_id: activityId,
                reason: "server_flag",
            })
        }
        lastDegradedRef.current = !!conversation.degraded
    }, [conversation.degraded, activityId])

    const sendText = useCallback(
        (message: string) => dispatchSend({ message, origin: "text", trigger: SAM_TRIGGERS.freeText }),
        [dispatchSend]
    )

    const askHint = useCallback(
        () => dispatchSend({ message: "Donne-moi un indice", trigger: SAM_TRIGGERS.hint }),
        [dispatchSend]
    )

    const askCourseReminder = useCallback(
        () =>
            dispatchSend({
                message: "Rappelle-moi la règle utile pour cet exercice",
                trigger: SAM_TRIGGERS.courseReminder,
            }),
        [dispatchSend]
    )

    const reset = useCallback(() => {
        if (activityId) dispatch(resetSamConversation(activityId))
    }, [activityId, dispatch])

    /** Renvoie le message resté sans réponse : il quitte le fil, l'envoi le repose. */
    const retryMessage = useCallback(
        (messageId: string) => {
            if (!activityId || failed == null || failed.id !== messageId) return
            dispatch(dropSamMessage(activityId, messageId))
            dispatchSend(failed.params)
        },
        [activityId, dispatch, dispatchSend, failed]
    )

    // Un message que le fil ne porte plus (réinitialisé, autre activité) n'est plus à renvoyer.
    const failedMessageId =
        failed != null && conversation.messages.some((message) => message.id === failed.id) ? failed.id : null

    return {
        ...conversation,
        capReached,
        capScope: dailyCapReached ? "daily" : capReached ? "activity" : null,
        sendText,
        askHint,
        askCourseReminder,
        reset,
        failedMessageId,
        retryMessage,
    }
}
